// Deal Details Modal component for DealerDashboard
import React, { useEffect, useState } from 'react';
import { Modal, Card, Row, Col, Typography, Button, Badge, Divider, Spin, Alert, List, Avatar, Empty, message } from 'antd';
import { 
  CloseOutlined, 
  CarOutlined, 
  DollarOutlined, 
  UserOutlined, 
  MessageOutlined, 
  FileTextOutlined,
  ExclamationCircleOutlined,
  PhoneOutlined,
  ScheduleOutlined,
  ShareAltOutlined
} from '@ant-design/icons';
import { formatName } from '../../util';
import { STAGE_COLORS, STAGE_DESCRIPTIONS } from './constants';
import { getDealerHostContact, fetchDealerMemberChat } from '../../actions/Actions';

const { Title, Text } = Typography;

const DealDetailsModal = ({ 
  dealDetailsModal, 
  setDealDetailsModal, 
  selectedRecord,
  fpDealerId
}) => {
  const [contactInfo, setContactInfo] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState(null);
  const [chatModalVisible, setChatModalVisible] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatError, setChatError] = useState(null);
  const [chatBadgeCount, setChatBadgeCount] = useState(null);

  const contactLookupName = (selectedRecord?.repliedDealerPros || selectedRecord?.salesRep || '').trim();

  useEffect(() => {
    if (!dealDetailsModal || !selectedRecord) {
      setContactInfo(null);
      setContactError(null);
      setContactLoading(false);
      return;
    }

    if (!fpDealerId || !contactLookupName || contactLookupName === '—') {
      setContactInfo(null);
      setContactError(null);
      setContactLoading(false);
      return;
    }

    let cancelled = false;
    const fetchContact = async () => {
      setContactLoading(true);
      setContactInfo(null);
      setContactError(null);
      try {
        const response = await getDealerHostContact(fpDealerId, contactLookupName);
        if (cancelled) return;
        if (response && response.status && response.data) {
          setContactInfo(response.data);
        } else {
          setContactInfo(null);
          setContactError(response?.message || null);
        }
      } catch (err) {
        if (!cancelled) {
          setContactInfo(null);
          setContactError('Unable to load dealer contact information.');
        }
      } finally {
        if (!cancelled) {
          setContactLoading(false);
        }
      }
    };

    fetchContact();

    return () => {
      cancelled = true;
    };
  }, [dealDetailsModal, fpDealerId, contactLookupName, selectedRecord]);

  const formatPhoneNumber = (value) => {
    if (!value) return '';
    const digits = String(value).replace(/[^0-9]/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return String(value);
  };

  const handleOpenChat = async () => {
    if (!selectedRecord) return;
    const sanitize = (value) => {
      if (value == null) return '';
      const str = String(value).trim();
      if (!str || str === '—' || str === '--' || str.toLowerCase() === 'na') return '';
      return str;
    };

    const loanId = sanitize(selectedRecord.loanId);
    const fastPassDealerId = sanitize(selectedRecord.fastPassDealerId || fpDealerId || selectedRecord.FastPassDealerId);
    const vehicleVin = sanitize(selectedRecord.vin);
    const fastpassVehicleId = sanitize(selectedRecord.fastpassVehicleId || selectedRecord.inventoryRowId || selectedRecord.row_id);

    if (!loanId || !fastPassDealerId || !vehicleVin || !fastpassVehicleId) {
      message.error('Missing data required to load the conversation.');
      return;
    }

    setChatModalVisible(true);
    setChatLoading(true);
    setChatError(null);
    setChatMessages([]);

    try {
      const response = await fetchDealerMemberChat({
        loanId,
        fastPassDealerId,
        vehicleVin,
        fastpassVehicleId,
        pageNumber: 1
      });

      const envelope = Array.isArray(response) ? response[0] : response;
      const hasError = envelope && envelope.error && Object.keys(envelope.error).length > 0;
      if (hasError) {
        setChatMessages([]);
        setChatError(typeof envelope.error === 'string' ? envelope.error : 'Unable to load the conversation.');
        return;
      }
      const success = envelope && envelope.success ? envelope.success : null;
      const messagesData = (success && Array.isArray(success.data)) ? success.data : [];

      setChatMessages(messagesData);
      setChatBadgeCount(messagesData.length);

      if (!success || messagesData.length === 0) {
        setChatError('No conversation history found for this member yet.');
      }
    } catch (err) {
      console.error('Failed to load dealer-member chat', err);
      setChatError('Unable to load the conversation at this time.');
    } finally {
      setChatLoading(false);
    }
  };

  const renderMessage = (item) => {
    const createdAt = item?.CreatedDate ? new Date(item.CreatedDate) : null;
    const formattedDate = createdAt && Number.isFinite(createdAt.getTime())
      ? createdAt.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })
      : '';

    const sender = item?.Sender || 'Member';
    const senderName = item?.SenderName || sender;
    const avatarLetter = senderName ? senderName.charAt(0).toUpperCase() : 'M';
    const isDealer = sender?.toLowerCase() === 'dealer';

    return (
      <List.Item
        style={{
          background: isDealer ? '#f6ffed' : '#fff1f0',
          border: '1px solid',
          borderColor: isDealer ? '#b7eb8f' : '#ffa39e',
          borderRadius: '12px',
          padding: '16px'
        }}
      >
        <List.Item.Meta
          avatar={<Avatar style={{ backgroundColor: isDealer ? '#52c41a' : '#ff7875' }}>{avatarLetter}</Avatar>}
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>{senderName}</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>{formattedDate}</Text>
            </div>
          }
          description={
            <div
              style={{ marginTop: '8px' }}
              dangerouslySetInnerHTML={{ __html: item?.EmailMessage || '' }}
            />
          }
        />
      </List.Item>
    );
  };

  if (!selectedRecord) return null;

  const getStageColorHex = (stage) => {
    return STAGE_COLORS[stage] || STAGE_COLORS.default;
  };

  const isFPProvided = selectedRecord.currentStage === 'FP Provided';
  const canViewDealSheet = isFPProvided;

  return (
    <>
      <Modal
      title={null}
      open={dealDetailsModal}
      onCancel={() => setDealDetailsModal(false)}
      footer={null}
      width={1200}
      style={{ top: 20 }}
      closable={false}
    >
      {/* Header */}
      <div style={{ 
        background: getStageColorHex(selectedRecord.currentStage), 
        color: 'white', 
        padding: '16px 24px', 
        margin: '-24px -24px 24px -24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <Title level={3} style={{ color: 'white', margin: 0 }}>
            {selectedRecord.vehicle}
          </Title>
          <Text style={{ color: 'white', fontSize: '14px' }}>
            Stock: {selectedRecord.stock} • VIN: {selectedRecord.vin}
          </Text>
        </div>
        <Button 
          type="text" 
          icon={<CloseOutlined />} 
          onClick={() => setDealDetailsModal(false)}
          style={{ color: 'white', fontSize: '18px' }}
        />
      </div>

      {/* Main Content with Better Spacing */}
      <div style={{ padding: '0 8px' }}>
        {/* Top Section - Key Info Cards */}
        <Row gutter={[20, 20]} style={{ marginBottom: '24px' }}>
          {/* Vehicle Info - Prominent */}
          <Col xs={24} lg={12}>
            <Card 
              style={{ 
                height: '100%',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: 'none'
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{ 
                  width: '48px', 
                  height: '48px', 
                  borderRadius: '12px', 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '16px'
                }}>
                  <CarOutlined style={{ fontSize: '24px', color: 'white' }} />
                </div>
                <div>
                  <Title level={4} style={{ margin: 0, color: '#262626' }}>Vehicle Details</Title>
                  <Text type="secondary">Complete vehicle information</Text>
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Make & Model</Text>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#262626', marginTop: '4px' }}>
                    {selectedRecord.vehicle.split(' ')[1]} {selectedRecord.vehicle.split(' ').slice(1).join(' ')}
                  </div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Year & Trim</Text>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#262626', marginTop: '4px' }}>
                    {selectedRecord.vehicle.split(' ')[0]} • {selectedRecord.trim.split(' • ')[0]}
                  </div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Color</Text>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#262626', marginTop: '4px' }}>
                    {selectedRecord.trim.split(' • ')[1]}
                  </div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Stock #</Text>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#262626', marginTop: '4px' }}>
                    {selectedRecord.stock}
                  </div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Condition</Text>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#262626', marginTop: '4px' }}>
                    {(selectedRecord.condition || '').toString()} {(() => {
                      const n = parseInt(String(selectedRecord.miles || '').replace(/[^0-9]/g, ''), 10);
                      return Number.isFinite(n) ? `• ${n.toLocaleString()} miles` : (selectedRecord.miles ? `• ${selectedRecord.miles}` : '');
                    })()}
                  </div>
                </div>
              </div>
              
              <Divider style={{ margin: '20px 0' }} />
              
              <div style={{ textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>MSRP</Text>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#52c41a', marginTop: '4px' }}>
                  {selectedRecord.msrp}
                </div>
              </div>
            </Card>
          </Col>

          {/* Deal Status - Prominent */}
          <Col xs={24} lg={12}>
            <Card 
              style={{ 
                height: '100%',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: 'none'
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{ 
                  width: '48px', 
                  height: '48px', 
                  borderRadius: '12px', 
                  background: `linear-gradient(135deg, ${getStageColorHex(selectedRecord.currentStage)} 0%, ${getStageColorHex(selectedRecord.currentStage)}CC 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '16px'
                }}>
                  <DollarOutlined style={{ fontSize: '24px', color: 'white' }} />
                </div>
                <div>
                  <Title level={4} style={{ margin: 0, color: '#262626' }}>Deal Status</Title>
                  <Text type="secondary">Current stage and progression</Text>
                </div>
              </div>

              {/* Complete Stage Timeline with Details */}
              <div style={{ marginBottom: '20px' }}>
                {/* Timeline Title */}
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <Text style={{ 
                    fontSize: '16px', 
                    fontWeight: '600', 
                    color: getStageColorHex(selectedRecord.currentStage),
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    DEAL JOURNEY TIMELINE
                  </Text>
                </div>
                
                {/* All Stages Timeline */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                  {[
                    { stage: 'Prospect Waiting', key: 'prospect', icon: <ExclamationCircleOutlined />, description: STAGE_DESCRIPTIONS['Prospect Waiting'] },
                    { stage: 'OTD$ Provided', key: 'otd', icon: <DollarOutlined />, description: STAGE_DESCRIPTIONS['OTD$ Provided'] },
                    { stage: 'Lead Waiting', key: 'lead', icon: <PhoneOutlined />, description: STAGE_DESCRIPTIONS['Lead Waiting'] },
                    { stage: 'TD Scheduled', key: 'td', icon: <ScheduleOutlined />, description: STAGE_DESCRIPTIONS['TD Scheduled'] },
                    { stage: 'FP Provided', key: 'fp', icon: <ShareAltOutlined />, description: STAGE_DESCRIPTIONS['FP Provided'] }
                  ].map((stageInfo, index) => {
                    const isCompleted = selectedRecord.stageHistory.some(s => s.stage === stageInfo.stage && s.completed);
                    const isCurrent = selectedRecord.currentStage === stageInfo.stage;
                    const isFuture = !isCompleted && !isCurrent;
                    
                    return (
                      <div key={stageInfo.key} style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        flex: 1,
                        position: 'relative'
                      }}>
                        {/* Stage Circle */}
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          background: isCompleted ? getStageColorHex(stageInfo.stage) : 
                                     isCurrent ? getStageColorHex(stageInfo.stage) : '#f0f0f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '8px',
                          border: isCurrent ? `3px solid ${getStageColorHex(stageInfo.stage)}` : 
                                 isCompleted ? `2px solid ${getStageColorHex(stageInfo.stage)}` : '2px solid #d9d9d9',
                          boxShadow: isCurrent ? `0 0 0 4px ${getStageColorHex(stageInfo.stage)}20` : 'none',
                          position: 'relative'
                        }}>
                          {isCompleted ? (
                            React.cloneElement(stageInfo.icon, { 
                              style: { color: 'white', fontSize: '20px' } 
                            })
                          ) : isCurrent ? (
                            <div style={{ position: 'relative' }}>
                              {React.cloneElement(stageInfo.icon, { 
                                style: { color: 'white', fontSize: '20px' } 
                              })}
                              <div style={{
                                position: 'absolute',
                                bottom: '-2px',
                                right: '-2px',
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                background: 'white',
                                border: `2px solid ${getStageColorHex(stageInfo.stage)}`,
                                animation: 'pulse 2s infinite'
                              }} />
                            </div>
                          ) : (
                            React.cloneElement(stageInfo.icon, { 
                              style: { color: '#999', fontSize: '20px' } 
                            })
                          )}
                        </div>
                        
                        {/* Stage Label */}
                        <div style={{ 
                          textAlign: 'center',
                          opacity: isFuture ? 0.4 : 1
                        }}>
                          <Text style={{ 
                            fontSize: '12px', 
                            fontWeight: '600',
                            color: isCurrent ? getStageColorHex(stageInfo.stage) : 
                                   isCompleted ? '#262626' : '#999',
                            lineHeight: '1.2',
                            marginBottom: '4px',
                            display: 'block'
                          }}>
                            {stageInfo.stage}
                          </Text>
                          <Text style={{ 
                            fontSize: '10px', 
                            color: isFuture ? '#999' : '#666',
                            fontWeight: '400',
                            marginBottom: '2px',
                            display: 'block'
                          }}>
                            {isCompleted || isCurrent ? 
                              selectedRecord.stageHistory.find(s => s.stage === stageInfo.stage)?.date || 'N/A' : 
                              'N/A'
                            }
                          </Text>
                          {/* Stage Description */}
                          <Text style={{ 
                            fontSize: '9px', 
                            color: isFuture ? '#999' : '#666',
                            fontWeight: '400',
                            display: 'block',
                            marginBottom: '2px'
                          }}>
                            {stageInfo.description}
                          </Text>
                          {/* Show amounts for relevant stages */}
                          {stageInfo.stage === 'OTD$ Provided' && selectedRecord.otdAmount && (isCompleted || isCurrent) && (
                            <Text style={{ 
                              fontSize: '10px', 
                              color: '#52c41a',
                              fontWeight: '600',
                              display: 'block'
                            }}>
                              {selectedRecord.otdAmount}
                            </Text>
                          )}
                        </div>
                        
                        {/* Connecting Line with Arrow */}
                        {index < 4 && (
                          <div style={{
                            position: 'absolute',
                            top: '24px',
                            left: 'calc(50% + 24px)',
                            width: 'calc(100% - 48px)',
                            height: '4px',
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            {/* Line */}
                            <div style={{
                              width: 'calc(100% - 12px)',
                              height: '4px',
                              background: isCompleted ? getStageColorHex(stageInfo.stage) : 
                                         isCurrent ? getStageColorHex(stageInfo.stage) : 
                                         '#d9d9d9',
                              borderRadius: '2px'
                            }} />
                            {/* Arrow */}
                            <div style={{
                              width: '0',
                              height: '0',
                              borderLeft: `8px solid ${isCompleted ? getStageColorHex(stageInfo.stage) : 
                                                            isCurrent ? getStageColorHex(stageInfo.stage) : 
                                                            '#d9d9d9'}`,
                              borderTop: '6px solid transparent',
                              borderBottom: '6px solid transparent',
                              marginLeft: '4px'
                            }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Bottom Section - Member & Actions */}
        <Row gutter={[20, 20]}>
          {/* Member Information */}
          <Col xs={24} lg={16}>
            <Card 
              style={{ 
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: 'none'
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{ 
                  width: '48px', 
                  height: '48px', 
                  borderRadius: '12px', 
                  background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '16px'
                }}>
                  <UserOutlined style={{ fontSize: '24px', color: 'white' }} />
                </div>
                <div>
                  <Title level={4} style={{ margin: 0, color: '#262626' }}>Member Information</Title>
                  <Text type="secondary">Customer details and loan information</Text>
                </div>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12}>
                  <div style={{ 
                    background: '#fafafa', 
                    padding: '16px', 
                    borderRadius: '8px',
                    border: '1px solid #f0f0f0'
                  }}>
                    <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Member Name</Text>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#262626', marginTop: '4px' }}>
                      {formatName(selectedRecord.memberDetails)} {isFPProvided ? formatName(selectedRecord.memberLastName) : ''}
                    </div>
                  </div>
                </Col>
                
                {isFPProvided && (
                  <>
                    <Col xs={24} sm={12}>
                      <div style={{ 
                        background: '#fafafa', 
                        padding: '16px', 
                        borderRadius: '8px',
                        border: '1px solid #f0f0f0'
                      }}>
                        <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>FP ID</Text>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#262626', marginTop: '4px' }}>
                          {selectedRecord.loanId}
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} sm={12}>
                      <div style={{ 
                        background: '#fafafa', 
                        padding: '16px', 
                        borderRadius: '8px',
                        border: '1px solid #f0f0f0'
                      }}>
                        <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>PAP Number</Text>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#262626', marginTop: '4px' }}>
                          PAP{selectedRecord.loanId.slice(-6)}
                        </div>
                      </div>
                    </Col>
                  </>
                )}
              </Row>

              {/* Sales Rep Info */}
              <Divider style={{ margin: '20px 0' }} />
              <div style={{ 
                background: '#f6ffed', 
                padding: '16px', 
                borderRadius: '8px',
                border: '1px solid #b7eb8f'
              }}>
                <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Dealer Contact</Text>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#262626', marginTop: '4px' }}>
                  {contactInfo?.name || selectedRecord.salesRep || '—'}
                </div>
                {contactInfo?.title && (
                  <Text type="secondary" style={{ display: 'block', marginTop: '4px' }}>
                    {contactInfo.title}
                  </Text>
                )}
                {contactLoading && (
                  <Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
                    Loading contact information…
                  </Text>
                )}
                {!contactLoading && contactError && (
                  <Text type="danger" style={{ display: 'block', marginTop: '8px', fontSize: '12px' }}>
                    {contactError}
                  </Text>
                )}
                {!contactLoading && !contactInfo && !contactError && contactLookupName && contactLookupName !== '—' && (
                  <Text type="secondary" style={{ display: 'block', marginTop: '8px', fontSize: '12px' }}>
                    No active dealer contact found for {contactLookupName}.
                  </Text>
                )}
                {contactInfo?.email && (
                  <div style={{ marginTop: '12px' }}>
                    <a href={`mailto:${contactInfo.email}`} style={{ color: '#1890ff' }}>
                      {contactInfo.email.toLowerCase()}
                    </a>
                  </div>
                )}
                {contactInfo?.phoneNumber && (
                  <div style={{ marginTop: '12px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <PhoneOutlined style={{ color: '#1890ff' }} />
                    <a href={`tel:${contactInfo.phoneNumber}`} style={{ color: '#262626', fontWeight: 500 }}>
                      {formatPhoneNumber(contactInfo.phoneNumber)}
                    </a>
                  </div>
                )}
                {contactInfo?.textNumber && (!contactInfo?.phoneNumber || contactInfo.textNumber !== contactInfo.phoneNumber) && (
                  <div style={{ marginTop: '8px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <MessageOutlined style={{ color: '#52c41a' }} />
                    <a href={`tel:${contactInfo.textNumber}`} style={{ color: '#262626', fontWeight: 500 }}>
                      {formatPhoneNumber(contactInfo.textNumber)}
                    </a>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Text</Text>
                  </div>
                )}
              </div>
            </Card>
          </Col>

          {/* Action Buttons */}
          <Col xs={24} lg={8}>
            <Card 
              style={{ 
                height: '100%',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: 'none'
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <Title level={4} style={{ margin: 0, color: '#262626' }}>Actions</Title>
                <Text type="secondary">Manage this deal</Text>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Button 
                  type="primary" 
                  icon={<MessageOutlined />}
                  size="large"
                  block
                  onClick={handleOpenChat}
                  style={{ 
                    height: '48px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}
                >
                  View Chat
                  <Badge 
                    count={chatBadgeCount || 0} 
                    size="small" 
                    style={{ marginLeft: '8px' }}
                    showZero={false}
                  />
                </Button>
                
                <Button 
                  type={canViewDealSheet ? "primary" : "default"}
                  icon={<FileTextOutlined />}
                  size="large"
                  block
                  disabled={!canViewDealSheet}
                  style={{ 
                    height: '48px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    backgroundColor: canViewDealSheet ? '#52c41a' : undefined,
                    borderColor: canViewDealSheet ? '#52c41a' : undefined
                  }}
                >
                  View Deal Sheet
                </Button>
                
                {!canViewDealSheet && (
                  <div style={{ 
                    background: '#fff7e6', 
                    padding: '12px', 
                    borderRadius: '8px',
                    border: '1px solid #ffd591',
                    textAlign: 'center'
                  }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Deal sheet available after FastPass is provided
                    </Text>
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </div>
      </Modal>

      <Modal
        title={null}
        open={chatModalVisible}
        onCancel={() => setChatModalVisible(false)}
        footer={null}
        width={800}
        bodyStyle={{ padding: '0' }}
        closable={false}
      >
      <div style={{
        background: '#002766',
        color: 'white',
        padding: '16px 24px',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={4} style={{ color: 'white', margin: 0 }}>Dealer Conversation</Title>
            <Text style={{ color: 'rgba(255,255,255,0.75)' }}>
              {selectedRecord.vehicle} • VIN {selectedRecord.vin}
            </Text>
          </div>
          <Button type="text" icon={<CloseOutlined />} style={{ color: 'white' }} onClick={() => setChatModalVisible(false)} />
        </div>
      </div>

      <div style={{ padding: '24px', maxHeight: '550px', overflowY: 'auto' }}>
        <Row gutter={16} style={{ marginBottom: '16px' }}>
          {selectedRecord.vehiclePhoto && (
            <Col span={8}>
              <div style={{
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid #f0f0f0',
                height: '120px',
                background: '#fafafa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img
                  src={selectedRecord.vehiclePhoto}
                  alt={selectedRecord.vehicle}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
            </Col>
          )}
          <Col span={selectedRecord.vehiclePhoto ? 16 : 24}>
            <Card bordered={false} style={{ background: '#f6f9ff' }}>
              <Text type="secondary" style={{ textTransform: 'uppercase', fontSize: '11px' }}>Conversation Details</Text>
              <div style={{ marginTop: '6px', fontWeight: 600, color: '#1d39c4' }}>Loan ID: {selectedRecord.loanId}</div>
              <div style={{ color: '#1d39c4' }}>FastPass Dealer ID: {selectedRecord.fastPassDealerId || fpDealerId || '—'}</div>
            </Card>
          </Col>
        </Row>

        {chatLoading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        )}

        {!chatLoading && chatError && (
          <Alert type="info" message={chatError} showIcon />
        )}

        {!chatLoading && !chatError && chatMessages.length === 0 && (
          <Empty description="No conversation history yet" />
        )}

        {!chatLoading && chatMessages.length > 0 && (
          <List
            itemLayout="horizontal"
            dataSource={chatMessages}
            renderItem={renderMessage}
            style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
          />
        )}
      </div>
      </Modal>
    </>
  );
};

export default DealDetailsModal;
